import logo from "../../assets/images/logo.svg";
import { useState } from "react";
import { MdVisibility } from "react-icons/md";
import { MdVisibilityOff } from "react-icons/md";
import {
  Card,
  Input,
  Select,
  Typography,
  Option,
} from "@material-tailwind/react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { CreateWalletValidation, CreateWalletForm } from "./walletValidations";

import ApiRoutes from "../../services/apiRoutes";
import { useMutation } from "react-query";
import LoadingButton from "../../components/common/buttons/LoadingButton";
import { handleErrorMessage } from "../../utility";
import { useNavigate } from "react-router-dom";
import { routerPaths } from "../../services/path";
import ErrorParagraph from "../../components/common/ErrorParagraph";
import SelectDate from "../../components/common/SelectDate";
import AuthService from "../../services/authService";
import { CreateWalletType } from "../../models/wallet/createWallet";
import axios from "axios";

import { toast } from "react-toastify";
import Logo from "../../components/common/Logo";

function CreateWallet() {
  const [pinShown, setPinShown] = useState(false);
  const navigation = useNavigate();
  const [gender, setGender] = useState("Male");
  const [birthDate, setBirthDate] = useState(new Date());

  const handleStateChange = (value: string | undefined) => {
    if (!value) return;
    console.log(value);
    setGender(value);
    setValue("Gender", value);
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    reset,
    setValue,
  } = useForm<CreateWalletForm>({
    resolver: yupResolver(CreateWalletValidation),
    mode: "onChange",
  });

  const createWalletMutation = useMutation(
    (data: CreateWalletType) =>
      axios.post(
        `${ApiRoutes.baseUrl}${ApiRoutes.createWallet}`,
        {},
        {
          params: data,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      ),
    {
      onSuccess: (data) => {
        console.log(data);
        if (data.data.statusCode === 200 || data.data.statusCode === 201) {
          reset();
          clearErrors();
          navigation(routerPaths.wallet);
          toast.success("Wallet created successfully");
          return;
        }
        handleErrorMessage(
          data.data.statusMessage,
          "Unable to register Wallet. Please try again later."
        );
      },
      onError: (error) => {
        console.log(error);
        handleErrorMessage(
          error,
          "Unable to register Wallet. Please try again later."
        );
      },
    }
  );

  const { isLoading } = createWalletMutation;

  const onSubmit = (data: CreateWalletForm) => {
    console.log(data);
    const user = AuthService.user?.data;
    const newWallet = {
      ...data,
      Dob: data.Dob.toISOString(),
      PhoneNumber: user?.phoneNumber,
      Email: user?.email,
      FirstName: user?.firstName,
      LastName: user?.lastName,
    };
    console.log(newWallet);
    createWalletMutation.mutate(newWallet);
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        setValue("Gender", gender);
        setValue("Dob", birthDate);

        handleSubmit(onSubmit)();
      }}
    >
      <div className="md:flex w-screen md:h-screen bg-gray-50">
        <div className="invisible md:visible bg-map-background bg-no-repeat w-3/6 relative flex flex-col justify-center items-center">
          <div className="  absolute md:top-10 md:left-20">
            <Logo />
          </div>
          <div className="text-4xl">
            <h1>It’s easier to get</h1>
            <h2 className="inline">started with{` `}</h2>
            <h2 className="inline font-extrabold text-primary">GIGM Boat</h2>
          </div>
        </div>
        <img className="ml-20 md:hidden" src={logo} alt="GIGM Logo" />
        <div className="flex items-center justify-center ">
          <div className="md:w-[60%] md:m-24 m-10">
            <h2 className=" text-2xl text-center">Create a Wallet</h2>
            <Card placeholder={undefined} color="transparent" shadow={false}>
              <div className="mt-8 mb-2 w-80 max-w-screen-lg sm:w-96 my-auto ">
                <div className="mb-0 flex flex-col gap-6">
                  <Select
                    placeholder={undefined}
                    label="Gender"
                    value={gender}
                    {...register("Gender")}
                    onChange={(value) => handleStateChange(value)}
                  >
                    <Option value={"Male"}>Male</Option>
                    <Option value={"Female"}>Female</Option>
                  </Select>

                  <Typography
                    placeholder={undefined}
                    variant="paragraph"
                    color="blue-gray"
                    className="-mb-3"
                  >
                    BVN
                  </Typography>
                  <Input
                    size="lg"
                    placeholder="00*********"
                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900 bg-white"
                    labelProps={{
                      className: "before:content-none after:content-none",
                    }}
                    crossOrigin={undefined}
                    {...register("Bvn", {
                      required: true,
                      minLength: 11,
                      maxLength: 11,
                    })}
                  />
                  {errors.Bvn && (
                    <ErrorParagraph message={errors.Bvn.message!} />
                  )}

                  <SelectDate
                    selectDateProps={{
                      className: "md:w-full md:m-0 mx-0",
                      label: "Date of Birth",
                      setDate: (date) => {
                        setValue("Dob", date);
                        setBirthDate(date);
                      },
                      date: birthDate,
                    }}
                  />

                  <Typography
                    placeholder={undefined}
                    variant="paragraph"
                    color="blue-gray"
                    className="-mb-5"
                  >
                    PIN
                  </Typography>
                  <Input
                    type={pinShown ? "text" : "password"}
                    size="lg"
                    placeholder="****"
                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900 bg-white"
                    labelProps={{
                      className: "before:content-none after:content-none",
                    }}
                    icon={
                      pinShown ? (
                        <MdVisibility onClick={() => setPinShown(false)} />
                      ) : (
                        <MdVisibilityOff onClick={() => setPinShown(true)} />
                      )
                    }
                    crossOrigin={undefined}
                    {...register("Pin")}
                  />
                  {errors.Pin && (
                    <ErrorParagraph message={errors.Pin.message!} />
                  )}
                  <Typography
                    placeholder={undefined}
                    variant="paragraph"
                    color="blue-gray"
                    className="-mb-5"
                  >
                    Confirm PIN
                  </Typography>
                  <Input
                    type={pinShown ? "text" : "password"}
                    size="lg"
                    placeholder="****"
                    className=" !border-t-blue-gray-200 focus:!border-t-gray-900 bg-white "
                    labelProps={{
                      className: "before:content-none after:content-none",
                    }}
                    icon={
                      pinShown ? (
                        <MdVisibility onClick={() => setPinShown(false)} />
                      ) : (
                        <MdVisibilityOff onClick={() => setPinShown(true)} />
                      )
                    }
                    crossOrigin={undefined}
                    {...register("confirmPin")}
                  />
                  {errors.confirmPin && (
                    <ErrorParagraph message={errors.confirmPin.message!} />
                  )}
                </div>
                <LoadingButton isLoading={isLoading}>
                  Create Wallet
                </LoadingButton>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </form>
  );
}

export default CreateWallet;
